import { memo, useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import {
  msgStyles,
  NoOptionsMessage,
} from '../../NoOptionsMessage/no-options-message-component';
import globals from '../../../Globals.json';
import api from '../../../Services/api';
import * as S from './styles';

// ... imports ...

interface NcmSelectProps {
  label?: string;
  state?: number;
  setState?: (value: number | undefined) => void;
  onChange?: (value: number | undefined) => void;
  isClearable?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onBlur?: () => void;
  minWidth?: string;

  // Props para CEST/NCM lógica
  cests?: any;
  cest?: number;
  ncm?: number;
  setCestListObject?: (value: any[]) => void;
}

const NcmSelect = ({
  label,
  state,
  setState,
  isClearable,
  placeholder,
  isDisabled,
  isInvalid,
  onChange,
  onBlur,
  minWidth,
  cests,
  cest,
  setCestListObject,
}: NcmSelectProps) => {
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [defaultOptions, setDefaultOptions] = useState<any[]>([]);
  const [cestListObject, setLocalCestListObject] = useState<any[]>([]);

  useEffect(() => {
    if (state) {
      api.get(`${globals.api.taxes}/ncms/${state}`).then((response) => {
        const item = response.data.data;
        setSelectedOption({
          value: item.id,
          label: `${item.codigo} - ${item.descricao}`,
          ncm_code: item.codigo,
        });
        const options = [response.data.data].map((item: any) => ({
          value: item.id,
          label: `${item.codigo} - ${item.descricao}`,
          ncm_code: item.codigo,
        }));

        setDefaultOptions(options);
      });
    } else {
      setSelectedOption(null);
    }
  }, [state]);

  useEffect(() => {
    api.get(`${globals.api.taxes}/ncms?limit=15`).then((response) => {
      const options = response.data.data.map((item: any) => ({
        value: item.id,
        label: `${item.codigo} - ${item.descricao}`,
        ncm_code: item.codigo,
      }));
      setDefaultOptions(options);
    });
  }, []);

  const loadOptions = async (inputValue: string) => {
    if (inputValue.length < 1) return [];
    const isNumber = !isNaN(Number(inputValue));
    const url = isNumber
      ? `${globals.api.taxes}/ncms?codigo=${inputValue}`
      : `${globals.api.taxes}/ncms?descricao=${inputValue}`;

    const { data } = await api.get(url);
    return data.data.map((item: any) => ({
      value: item.id,
      label: `${item.codigo} - ${item.descricao}`,
      ncm_code: item.codigo,
    }));
  };

  const handleChange = (option: any) => {
    setSelectedOption(option);
    const value = option?.value ?? undefined;
    if (onChange) {
      onChange(value);
    } else {
      setState?.(value);
    }
  };

  useEffect(() => {
    const ncmSelected = defaultOptions.find(
      (item: any) => item?.ncm_code === selectedOption?.ncm_code
    );

    if (cests?.data && ncmSelected) {
      const filtered = cests.data.filter(
        (item: any) =>
          Number(item?.relationships?.ncm?.codigo) ===
          Number(ncmSelected?.ncm_code)
      );

      setLocalCestListObject(filtered);
      setCestListObject?.(filtered);
    }
  }, [cests?.data, defaultOptions, selectedOption?.ncm_code]);

  useEffect(() => {
    const cestObjectEdit = cests?.data?.find(
      (item: any) => Number(item.value) === Number(cest)
    );
    if (
      cestObjectEdit &&
      !cestListObject.some((item: any) => item.value === cestObjectEdit.value)
    ) {
      const updated = [...cestListObject, cestObjectEdit];
      setLocalCestListObject(updated);
      setCestListObject?.(updated);
    }
  }, [cestListObject]);

  return (
    <S.Content>
      <label>
        <div className="label">{label}</div>
        <S.Input>
          <S.Wrapper minWidth={minWidth}>
            <div className="select" data-invalid={isInvalid ? 'true' : undefined} aria-invalid={isInvalid ? 'true' : undefined}>
              <AsyncSelect
                cacheOptions
                defaultOptions={defaultOptions}
                loadOptions={loadOptions}
                value={selectedOption}
                onChange={handleChange}
                isClearable={true}
                placeholder={
                  <div className="placeholderSelect">{placeholder}</div>
                }
                components={{ NoOptionsMessage }}
                isDisabled={isDisabled}
                onBlur={onBlur}
                styles={{
                  noOptionsMessage: (base) => ({ ...base, ...msgStyles }),
                  control: (base) => ({
                    ...base,
                    border: isInvalid
                      ? '1px solid #F1416C'
                      : '1px solid var(--color-input-border)',
                    boxShadow: 'var(--color-input-shadow)',
                    borderRadius: 4,
                    height: 18,
                    display: 'flex',
                    alignContent: 'center',
                    minHeight: 26.5,
                    textAlign: 'left',
                  }),
                }}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary50: '#e4e6ef',
                    primary25: '#e4e6ef',
                    primary75: '#e4e6ef',
                    primary: '#5d98f1e8',
                  },
                })}
              />
            </div>
          </S.Wrapper>
        </S.Input>
      </label>
    </S.Content>
  );
};

export default memo(NcmSelect);
